// A zip of the photographs attached to a set of incidents — part of the
// post-event wrap-up (see Incidents.wrapUp in the backend's permissions matrix).
//
// The photos live inside the rich text, not beside it: one is a Markdown image
// reference in an incident's details or in one of its notes, pointing at the
// Document row that holds the bytes (core/photos.py). So the set of files to
// fetch is whatever richText.imageUrls finds, in the order they were written.
//
// Deliberately independent of the consent filter next to it on the page. The zip
// is of whatever is listed — filter to the patients who consented to their
// photographs being used and you get those; don't, and you get the event's. Tying
// the two together would make the honest use (pulling the photos off one
// incident) impossible and the careless one no harder.

import axios from "axios";

import { API_BASE_URL } from "./config";
import { downloadBlob, fileSlug } from "./download";
import { imageUrls } from "./richText";

// Extension for the type the bytes actually came back as, rather than trusting
// the URL — a photo is served under /incidents/photos/<id>, which carries none.
// Only what the uploader accepts (images) can be here; anything unrecognised
// keeps a generic extension rather than none, so the file still opens by
// double-clicking on Windows.
const EXTENSIONS = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/heic": "heic",
};
const extensionFor = (type) => EXTENSIONS[(type || "").toLowerCase()] || "img";

/**
 * Every photo on one incident, in the order they appear: the details first, then
 * each note oldest first — the order they were added to the record.
 */
const incidentPhotoUrls = (i) => [
  ...imageUrls(i.incident_details),
  ...(i.notes || [])
    .slice()
    .sort((a, b) => (a.added_at || "").localeCompare(b.added_at || ""))
    .flatMap(n => imageUrls(n.details)),
];

/**
 * One photo's name inside the zip: the incident's id, then its summary, then the
 * position within that incident when it has more than one.
 *
 * The id leads because it is what the photo has to be traced back to — a folder
 * of images is no use if nobody can say which record each came off — and the
 * summary follows because an id alone is unreadable when the folder is being
 * looked through by a person. A lone photo takes no number: "12-Ankle_injury-1"
 * reads as though there were others.
 */
const photoFilename = (incident, index, total, contentType) => {
  const stem = [
    incident.id ?? "incident",
    fileSlug(incident.incident_summary, "incident"),
    total > 1 ? index + 1 : null,
  ].filter(v => v != null).join("-");
  return `${stem}.${extensionFor(contentType)}`;
};

/**
 * Download every photo on `incidents` as one zip, named after `event`.
 *
 * Resolves to `{ saved, failed }`. A photo that can't be fetched is counted and
 * skipped rather than failing the export — with a hundred images in the zip, one
 * missing document is not a reason to produce nothing. The requests suppress the
 * global error toast for the same reason: the caller reports the total once.
 *
 * Fetched one at a time on purpose. This runs over a whole event's incidents, and
 * firing a few hundred parallel requests at the API to build a file nobody is
 * waiting on to the second is a poor trade.
 */
export const exportIncidentPhotos = async (incidents, event) => {
  const withPhotos = incidents
    .map(i => ({ incident: i, urls: incidentPhotoUrls(i) }))
    .filter(x => x.urls.length > 0);
  if (withPhotos.length === 0) return { saved: 0, failed: 0 };

  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();
  let saved = 0;
  let failed = 0;

  for (const { incident, urls } of withPhotos) {
    for (let n = 0; n < urls.length; n += 1) {
      const url = urls[n];
      const full = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.get(full, { responseType: "blob", suppressErrorToast: true });
        zip.file(photoFilename(incident, n, urls.length, res.data?.type), res.data);
        saved += 1;
      } catch {
        failed += 1;
      }
    }
  }

  if (saved === 0) return { saved, failed };
  const label = event?.label_short || event?.abbreviation || event?.name;
  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(blob, `${fileSlug(label, "incidents")}-incident-photos.zip`);
  return { saved, failed };
};
